import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { AuthFormProvider } from "../context/AuthFormContext";
import { useAuthState } from "../hooks/useAuthState";
import { RegisterPayload, User } from "../services/authService";
import { getAuthService } from "../services/factory";
import { trackAuthGoal, isSkipPhoneVerification, getAbVariant } from "../utils/metrika";
import { trackTelemetry, trackAuthSuccess } from "../utils/telemetry";
import AuthModalLayout from "./auth/AuthModalLayout";
import AuthViewRouter from "./auth/AuthViewRouter";

type AuthView =
  | "identifier"
  | "login"
  | "register"
  | "reset"
  | "phone-otp"
  | "password-recovery-request"
  | "password-recovery-email-sent"
  | "password-reset"
  | "password-reset-success";

interface AuthModalProps {
  onClose: () => void;
  initialView?: string;
}

const AuthModal: React.FC<AuthModalProps> = ({
  onClose,
  initialView = "identifier",
}) => {
  const auth = useAuth();
  const {
    state,
    login,
    register,
    requestPasswordReset,
    resetPasswordWithToken,
    reset,
  } = useAuthState();
  const [currentView, setCurrentView] = useState<AuthView>(
    initialView as AuthView,
  );
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState<
    "email" | "phone" | "unknown"
  >("unknown");
  const [phoneExists, setPhoneExists] = useState<boolean | null>(null);
  const [isCheckingIdentifier, setIsCheckingIdentifier] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");

  // Преобразование строки представления в тип AuthView
  const mapViewStringToAuthView = (view: string): AuthView => {
    switch (view) {
      case "login":
      case "register":
      case "reset":
      case "phone-otp":
      case "password-recovery-request":
      case "password-recovery-email-sent":
      case "password-reset":
      case "password-reset-success":
        return view;
      case "reset-password":
        return "reset";
      case "phone-auth":
        return "phone-otp";
      default:
        return "identifier";
    }
  };

  // Reset auth state when modal opens
  useEffect(() => {
    reset();
  }, [reset]);

  // Sync initialView prop with internal state
  useEffect(() => {
    const mappedView = mapViewStringToAuthView(initialView);
    setCurrentView(mappedView);
  }, [initialView]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden"; // block scroll
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [handleEscape]);

  const handleSwitchView = useCallback(
    (view: AuthView) => {
      reset(); // Clear any previous auth state
      setCurrentView(view);
    },
    [reset],
  );

  useEffect(() => {
    trackAuthGoal("AUTH_MODAL_OPEN", { initial_view: initialView });
    // Телеметрия sk_analytics: шаг «Начата авторизация» (воронка продаж, шаг 2)
    trackTelemetry("auth_start", { initial_view: initialView });
  }, [initialView]);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      console.log("Attempting login with email:", email);

      const user = await login(email, password);
      if (user) {
        auth.login(user);
        trackAuthGoal("AUTH_SUCCESS", {
          method: "password",
          flow: "login",
          is_new_user: false,
        });
        trackAuthSuccess({ method: "password", flow: "login", is_new_user: false });
        onClose();
      }
    },
    [login, auth, onClose],
  );

  const handleRegister = useCallback(
    async (payload: RegisterPayload) => {
      const user = await register({
        ...payload,
        abVariant: getAbVariant(),
      });

      if (user) {
        auth.login(user);
        trackAuthGoal("AUTH_SUCCESS", {
          method: "register_form",
          flow: "standard_a",
          is_new_user: true,
        });
        trackAuthSuccess({ method: "register_form", flow: "standard_a", is_new_user: true });
        onClose();
      }
    },
    [register, auth, onClose],
  );

  const handleRequestPasswordReset = useCallback(
    async (email: string) => {
      setRecoveryEmail(email);
      const success = await requestPasswordReset(email);
      if (success) {
        setCurrentView("password-recovery-email-sent");
      }
    },
    [requestPasswordReset],
  );

  const handleResetPasswordWithToken = useCallback(
    async (token: string, newPassword: string) => {
      const success = await resetPasswordWithToken(token, newPassword);
      if (success) {
        setCurrentView("password-reset-success");
      }
    },
    [resetPasswordWithToken],
  );

  const handleIdentifier = async (
    normalizedIdentifier: string,
    type: "email" | "phone" | "unknown",
  ) => {
    console.log(
      `Identifier submitted: ${normalizedIdentifier} (type: ${type})`,
    );
    trackAuthGoal("AUTH_IDENTIFIER_SUBMIT", { type });

    // Store normalized identifier and type in local state
    setIdentifier(normalizedIdentifier);
    setIdentifierType(type);

    // Block UI while checking identifier
    setIsCheckingIdentifier(true);
    try {
      const authService = getAuthService();
      const result = await authService.checkIdentifier(normalizedIdentifier);

      if (type === "email") {
        // Email flow: route based on existence
        setCurrentView(result.exists ? "login" : "register");
      } else if (type === "phone") {
        // Проверяем настройку пропуска подтверждения номера для тестов
        const skipPhoneVerification = isSkipPhoneVerification();
        if (skipPhoneVerification) {
          try {
            const otpResult = await authService.verifyOtp(
              normalizedIdentifier,
              "000000",
            );
            if (otpResult.user && otpResult.user.id) {
              auth.login(otpResult.user);
              trackAuthGoal("AUTH_SUCCESS", {
                method: "skip_otp",
                flow: result.exists ? "login" : "quick_b",
                is_new_user: !result.exists,
              });
              trackAuthSuccess({ method: "skip_otp", flow: result.exists ? "login" : "quick_b", is_new_user: !result.exists });
              onClose();
              return;
            } else {
              // Variant A: новый пользователь сразу переходит к форме регистрации
              setCurrentView("register");
              setPhoneExists(null);
              return;
            }
          } catch (e) {
            console.error("Error during skipPhoneVerification OTP:", e);
            if (result.exists) {
              setCurrentView("login");
            } else {
              setCurrentView("register");
            }
            return;
          }
        }

        // Phone flow: always go to OTP, store existence for post-OTP routing
        setCurrentView("phone-otp");
        setPhoneExists(result.exists);
      }
      // If type is 'unknown', stay on identifier view (error shown in IdentifierView)
    } catch (error) {
      console.error("Error checking identifier:", error);
      // On error, default to allowing registration (exists: false)
      if (type === "email") {
        setCurrentView("register");
      } else if (type === "phone") {
        if (isSkipPhoneVerification()) {
          setCurrentView("register");
        } else {
          setCurrentView("phone-otp");
        }
        setPhoneExists(false);
      }
    } finally {
      setIsCheckingIdentifier(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    // Verify OTP using auth service
    const authService = getAuthService();
    const result = await authService.verifyOtp(identifier, code);

    if (!result.success) {
      throw new Error(result.errors?.[0] || "OTP verification failed");
    }

    // Successful OTP verification
    if (result.user && result.user.id) {
      // Existing user or quick-registered via Variant B
      auth.login(result.user);
      trackAuthGoal("AUTH_SUCCESS", {
        method: "phone_otp",
        flow: phoneExists === false ? "quick_b" : "login",
        is_new_user: phoneExists === false,
      });
      trackAuthSuccess({ method: "phone_otp", flow: phoneExists === false ? "quick_b" : "login", is_new_user: phoneExists === false });
      onClose();
    } else if (phoneExists === false) {
      // Variant A: new phone -> registration view
      setCurrentView("register");
      setPhoneExists(null);
    } else {
      // Should not happen: successful OTP but no user
      throw new Error("OTP verification succeeded but no user data received");
    }
  };

  const handleMobileIdSuccess = useCallback(
    (user?: User, isNewUser?: boolean) => {
      if (user && user.id) {
        // Existing user or quick-registered via Variant B
        auth.login(user);
        trackAuthGoal("AUTH_SUCCESS", {
          method: "mobile_id",
          flow: isNewUser ? "quick_b" : "login",
          is_new_user: !!isNewUser,
        });
        trackAuthSuccess({ method: "mobile_id", flow: isNewUser ? "quick_b" : "login", is_new_user: !!isNewUser });
        onClose();
      } else if (phoneExists === false || isNewUser || !user || !user.id) {
        // Variant A: new phone -> registration view
        setCurrentView("register");
        setPhoneExists(null);
      }
    },
    [phoneExists, auth, onClose],
  );

  const getModalTitle = (): string => {
    switch (currentView) {
      case "identifier":
        return "Вход или регистрация";
      case "login":
        return "Введите пароль";
      case "register":
        return "Регистрация";
      case "phone-otp":
        return "Подтвердите вход";
      case "password-recovery-request":
        return "Восстановление пароля";
      case "password-recovery-email-sent":
        return "Письмо отправлено";
      case "password-reset":
        return "Новый пароль";
      case "password-reset-success":
        return "Пароль изменён";
      case "reset":
        return "Сброс пароля";
      default:
        return "Вход или регистрация";
    }
  };

  const isRootView = currentView === "identifier";

  const handleBack = () => {
    switch (currentView) {
      case "login":
      case "register":
      case "phone-otp":
      case "password-recovery-request":
      case "reset":
        setCurrentView("identifier");
        break;

      case "password-recovery-email-sent":
        setCurrentView("password-recovery-request");
        break;

      case "password-reset":
        setCurrentView("password-recovery-request");
        break;

      case "password-reset-success":
        setCurrentView("login");
        break;

      default:
        break;
    }
  };

  const authFormValue = useMemo(
    () => ({
      state,
      login: handleLogin,
      register: handleRegister,
      requestPasswordReset: handleRequestPasswordReset,
      resetPasswordWithToken: handleResetPasswordWithToken,
      reset,
    }),
    [
      state,
      handleLogin,
      handleRegister,
      handleRequestPasswordReset,
      handleResetPasswordWithToken,
      reset,
    ],
  );

  return (
    <AuthFormProvider value={authFormValue}>
      <AuthModalLayout
        onClose={onClose}
        onBack={handleBack}
        showBackButton={!isRootView}
        title={getModalTitle()}
      >
        <AuthViewRouter
          currentView={currentView}
          onSwitchView={handleSwitchView}
          onContinue={handleIdentifier}
          onVerify={handleVerifyOtp}
          onMobileIdSuccess={handleMobileIdSuccess}
          identifier={identifier}
          identifierType={identifierType}
          phoneExists={phoneExists}
          isCheckingIdentifier={isCheckingIdentifier}
          phone={identifier}
          recoveryEmail={recoveryEmail}
        />
      </AuthModalLayout>
    </AuthFormProvider>
  );
};

export default AuthModal;
